// Ported from dev-scrips-pm-react src/documents/services/use-document-uploaded-by.ts (PROD-1119 cross-module closure).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { UploadedByData } from '@/documents/types/document';

/**
 * GET /api/Patients/GetDocumentUploadedBy?patientId={patientId}
 *
 * Returns the list of operators (`{userId, name}`) who have uploaded
 * documents for a given patient. Consumed by the Phase 2 filter widget
 * to populate the "Uploaded by" dropdown.
 *
 * Mirrors Flutter `DocumentDataSourceImpl.getDocumentUploadedBy`
 * (`document_data_source_impl.dart:75-90`). Sole Flutter dispatcher
 * is `documents_filter_widget.dart:49` (initState).
 *
 * Wire-shape notes:
 *   - Query param `patientId` (lowercase) — verbatim from Flutter
 *     `queryParameters: {"patientId": patientId}` at
 *     `document_data_source_impl.dart:80`. Note this differs from
 *     `?PatientId=` (uppercase) on `/Payment/AdvancePaymentReceipts`
 *     (patients PR #273) — per-endpoint casing is verbatim, not
 *     normalised.
 *   - Response is a flat array of `{userId, name}` (`UploadedByData`).
 *     Wire JSON key is `userId` (NOT `id`) — the Dart class internally
 *     renames the property to `uploadedById`, but the wire field is
 *     `userId`. PR #265 corrected the TS interface from `id` → `userId`.
 *
 * `enabled: !!patientId` gate. No-op when patient not yet loaded.
 *
 * AbortSignal forwarded per project rule (every queryFn destructures
 * `{ signal }` and passes to api-client).
 */
export function useDocumentUploadedBy(
  patientId: string | null | undefined,
): UseQueryResult<UploadedByData[]> {
  return useQuery<UploadedByData[]>({
    queryKey: ['document-uploaded-by', patientId],
    enabled: !!patientId,
    queryFn: ({ signal }) =>
      get<UploadedByData[]>(
        `/api/Patients/GetDocumentUploadedBy?patientId=${encodeURIComponent(patientId!)}`,
        { signal },
      ),
  });
}
